import {useMutation, useQuery} from "@apollo/client";
import {useEffect, useState} from "react";
import {Helmet} from "react-helmet-async";
import {useNavigate, useSearchParams} from "react-router";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {useGTM} from "@/components/trackers/GoogleTagEvents.ts";
import {SIGNUP_QUERY_PARAM} from "@/lib/constants/account.ts";
import {ANALYZER} from "@/lib/featureFlags.ts";
import {
  GQL_ACCOUNT_SIGNUP_ANALYTICS_DATA_QUERY,
  GQL_ACCOUNT_SIGNUP_ANALYTICS_DATA_UPDATE,
} from "@/lib/queries/accounts.ts";
import {AccountSignupAnalyticsDataResult, OnboardingWizardStep} from "@/lib/types/accounts.ts";
import {ResponseStatus} from "@/lib/types/common.ts";

import {ProgressBar} from "./components/progressbar/ProgressBar.tsx";
import {ScrollButton} from "./components/scrollbutton/ScrollButton.tsx";
import {Step} from "./components/step/Step.tsx";
import {STEP_ALERTS, STEP_ALERTS_WELCOME} from "./components/stepalerts/stepAlerts.tsx";
import {STEP_CONNECTED} from "./components/stepconnected/stepConnected.tsx";
import {STEP_TEAM} from "./components/stepteam/stepTeam.tsx";
import {STEP_WELCOME} from "./components/stepwelcome/stepWelcome.tsx";

import styles from "./OnboardingWizardPage.module.scss";

const LEGACY_STEPS = [STEP_WELCOME, STEP_ALERTS, STEP_TEAM, STEP_CONNECTED];
const ASSISTANT_STEPS = [STEP_ALERTS_WELCOME, STEP_TEAM, STEP_CONNECTED];

export const OnboardingWizardPage = () => {
  const {user, userHasFeature} = useCurrentUserContext();
  const assistantEnabled = !!userHasFeature(ANALYZER);
  const steps = assistantEnabled ? ASSISTANT_STEPS : LEGACY_STEPS;
  const [searchParams] = useSearchParams();
  useGTM(user);
  const {data, loading} = useQuery<AccountSignupAnalyticsDataResult>(GQL_ACCOUNT_SIGNUP_ANALYTICS_DATA_QUERY);
  const [updateAccountSignupAnalyticsData] = useMutation(GQL_ACCOUNT_SIGNUP_ANALYTICS_DATA_UPDATE);

  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [step, setStep] = useState(0);
  const [analyticsErrorMessage, setAnalyticsErrorMessage] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    if (initialLoadComplete || loading) return;
    if (data?.accountSignupAnalyticsData) {
      const savedStep = steps.findIndex((step) => step.name === data.accountSignupAnalyticsData.onboarding_wizard_step);
      setStep(Math.max(savedStep, 0));
    }
    setInitialLoadComplete(true);
  }, [data, initialLoadComplete, loading, setStep, steps]);

  const getRedirectUrl = () => {
    const params = new URLSearchParams({[SIGNUP_QUERY_PARAM]: "1"});
    if (!assistantEnabled) {
      return `/dashboard?${params}`;
    }
    const siteurl = searchParams.get("siteurl");
    if (siteurl) {
      params.set("url", siteurl);
    }
    return `/assistant/checks/create?${params}`;
  };

  const onNextStep = (nextStep: number) => {
    if (nextStep === steps.length) {
      const redirectUrl = getRedirectUrl();
      navigate(redirectUrl);
    } else {
      setStep(nextStep);
      window.scrollTo({top: 0, behavior: "instant"});
    }
  };

  const updateAnalyticsData = async (stepName: OnboardingWizardStep) => {
    setAnalyticsErrorMessage("");
    const updateResult = await updateAccountSignupAnalyticsData({
      variables: {
        data: {
          onboarding_wizard_step: stepName,
        },
      },
    });
    if (updateResult?.data?.response?.status === ResponseStatus.SUCCESS) {
      return true;
    }
    setAnalyticsErrorMessage("Something went wrong, please contact support.");
    return false;
  };

  const submitStep = async (nextStep: number, updateAnalytics: boolean) => {
    if (updateAnalytics) {
      const updateSuccess = await updateAnalyticsData(
        nextStep === steps.length ? OnboardingWizardStep.DONE : steps[nextStep].name
      );
      if (!updateSuccess) return;
    }
    onNextStep(nextStep);
  };

  const renderStep = () => {
    if (!initialLoadComplete) return;
    if (steps[step].title.includes("%name%")) {
      steps[step].title = steps[step].title.replace("%name%", user?.first_name ?? "");
    }
    return (
      <Step
        {...steps[step]}
        onSubmit={submitStep}
        analyticsErrorMessage={analyticsErrorMessage}
        setAnalyticsErrorMessage={setAnalyticsErrorMessage}
        stepsCount={steps.length}
        activeStepNr={step}
      />
    );
  };

  return (
    <div className={styles.wizard}>
      <Helmet>
        <title>Onboarding</title>
      </Helmet>
      <div className={styles.topSection}>
        <div className={styles.progressBar}>
          <ProgressBar stepsCount={steps.length} activeStepNr={step} />
        </div>
      </div>
      <main>{renderStep()}</main>
      <ScrollButton />
    </div>
  );
};
